import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Send, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-xl border border-white/10 bg-ink/70 px-4 py-3.5 text-sm sm:text-base text-slate-100 placeholder:text-slate-500 outline-none transition-colors duration-300 focus:border-accent/60 focus:bg-ink";

export const ContactForm = () => {
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "", company: "" });
  const [status, setStatus] = useState("idle");

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      const res = await fetch(`${API}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("send failed");
      setStatus("sent");
    } catch {
      setStatus("error");
    }
  };

  return (
    <section id="contact" data-testid="contact-form-section" className="relative py-24 lg:py-32 border-t border-white/[0.06] surface-weave">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
        <Reveal className="lg:col-span-5">
          <Eyebrow>No Voicemail Tag</Eyebrow>
          <h2 className="mt-5 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white text-balance">
            Can't Call Right Now? <span className="accent-text">Write To Us</span>
          </h2>
          <p className="mt-5 text-sm sm:text-base text-slate-400 leading-relaxed max-w-md">
            If you're at the scene, at work, or it's after hours — leave your details and a brief
            description of the damage. A master technician reviews every message and reaches back
            out, usually within one business hour.
          </p>
          <ul className="mt-8 space-y-3.5">
            {["Free, zero-obligation estimates", "Direct insurance claim handling", "Response within one business hour"].map((t) => (
              <li key={t} className="flex items-center gap-3 text-sm text-slate-300">
                <span className="h-1.5 w-1.5 rotate-45 bg-accentdeep shrink-0" aria-hidden="true" />
                {t}
              </li>
            ))}
          </ul>
        </Reveal>

        <Reveal className="lg:col-span-7" delay={0.12}>
          <div className="rounded-2xl border border-white/[0.08] bg-surface p-7 sm:p-10">
            <AnimatePresence mode="wait">
              {status === "sent" ? (
                <motion.div
                  key="sent"
                  data-testid="contact-form-success"
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                  className="flex flex-col items-center text-center py-14"
                >
                  <CheckCircle2 size={48} className="text-accent" strokeWidth={1.4} />
                  <h3 className="mt-6 font-display text-2xl font-bold text-white">Message Received</h3>
                  <p className="mt-3 max-w-sm text-sm sm:text-base text-slate-400 leading-relaxed">
                    Thank you — your enquiry is with our team. Expect a call or reply within one
                    business hour. For urgent towing situations, call (214) 888-2886.
                  </p>
                </motion.div>
              ) : (
                <motion.form
                  key="form"
                  data-testid="contact-form"
                  onSubmit={submit}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="grid grid-cols-1 sm:grid-cols-2 gap-5"
                >
                  <div>
                    <label htmlFor="cf-name" className="mb-2 block font-mono text-[10px] tracking-[0.24em] uppercase text-mute">Full Name *</label>
                    <input id="cf-name" data-testid="contact-form-name-input" required value={form.name} onChange={set("name")} placeholder="James Hunt" className={inputCls} maxLength={120} />
                  </div>
                  <div>
                    <label htmlFor="cf-phone" className="mb-2 block font-mono text-[10px] tracking-[0.24em] uppercase text-mute">Phone</label>
                    <input id="cf-phone" data-testid="contact-form-phone-input" type="tel" value={form.phone} onChange={set("phone")} placeholder="(214) 555-0100" className={inputCls} maxLength={40} />
                  </div>
                  <div className="sm:col-span-2">
                    <label htmlFor="cf-email" className="mb-2 block font-mono text-[10px] tracking-[0.24em] uppercase text-mute">Email *</label>
                    <input id="cf-email" data-testid="contact-form-email-input" type="email" required value={form.email} onChange={set("email")} placeholder="you@example.com" className={inputCls} />
                  </div>
                  <div className="sm:col-span-2">
                    <label htmlFor="cf-message" className="mb-2 block font-mono text-[10px] tracking-[0.24em] uppercase text-mute">Vehicle &amp; Damage Details *</label>
                    <textarea id="cf-message" data-testid="contact-form-message-input" required rows={4} value={form.message} onChange={set("message")} placeholder="2022 Porsche 911 — rear bumper and quarter panel damage, other driver insured by…" className={`${inputCls} resize-none`} maxLength={4000} />
                  </div>
                  {/* honeypot — hidden from humans */}
                  <div className="hidden" aria-hidden="true">
                    <label htmlFor="cf-company">Company</label>
                    <input id="cf-company" tabIndex={-1} autoComplete="off" value={form.company} onChange={set("company")} />
                  </div>
                  <div className="sm:col-span-2 flex flex-col sm:flex-row items-start sm:items-center gap-5">
                    <button
                      type="submit"
                      data-testid="contact-form-submit-button"
                      disabled={status === "sending"}
                      className="group inline-flex items-center gap-3 rounded-full bg-accentdeep px-8 py-4 font-display font-bold text-sm sm:text-base text-white transition-all duration-300 hover:bg-accenthover hover:-translate-y-0.5 disabled:opacity-60 disabled:hover:translate-y-0"
                    >
                      {status === "sending" ? <Loader2 size={17} className="animate-spin" /> : <Send size={16} strokeWidth={2.2} />}
                      {status === "sending" ? "Sending…" : "Get a Free Estimate"}
                    </button>
                    {status === "error" && (
                      <p data-testid="contact-form-error" className="inline-flex items-center gap-2 text-sm text-red-400">
                        <AlertCircle size={15} />
                        Something went wrong — please call (214) 888-2886 instead.
                      </p>
                    )}
                  </div>
                </motion.form>
              )}
            </AnimatePresence>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
